'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { NavMarker } from 'react-rhumb';
import { throttle } from 'underscore';
import styled from 'foundations-theming/utils';
import { css } from 'styled-components';
import { Box } from 'foundations-components/transitional/components/Box';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { View } from 'foundations-components/components/View';
import DeadSpaceDropZone from 'FormsUI/components/editor/form/dropZone/DeadSpaceDropZoneContainer';
import FormFieldsPanel from 'FormsUI/components/editor/form/formFields/FormFieldsPanel';
import QueuedFieldsPanel from 'FormsUI/components/editor/form/formFields/QueuedFieldsPanel';
import PropertiesContainer from 'FormsUI/containers/editor/leftPanel/PropertiesContainer';
import CustomDragLayer from 'FormsCommonComponents/editor/draggable/CustomDragLayer';
import CustomFieldDragLayer from 'FormsUI/components/editor/form/properties/newFields/CustomFieldDragLayer';
import { SpecialFieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { FieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { scrollToPosition } from 'FormsUI/utils/AnimationUtils';
import FormTypes from 'forms-utils-lib/constants/formDefinition/FormTypes';
import getFormFieldByType from 'FormsUI/components/editor/form/getFormFieldByType';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Wrapper = styled.div.withConfig({
  displayName: "FormPanel__Wrapper"
})(["position:relative;height:", ";overflow:", ";"], props => props.height ? `${props.height}px` : 'auto', props => props.overflow);
const SecondaryPanelContainer = styled.div.withConfig({
  displayName: "FormPanel__SecondaryPanelContainer"
})(({
  hasContent
}) => {
  return css(["z-index:2;position:absolute;top:0;left:0;width:100%;height:100%;transform:", ";transition:transform 0.3s ease-out;"], hasContent ? 'translateX(0%)' : 'translateX(-100%)');
});
const EditorPanel = styled.div.withConfig({
  displayName: "FormPanel__EditorPanel"
})(["height:", ";overflow:", ";max-width:60vw;"], props => props.height ? `${props.height}px` : 'auto', props => props.overflow);
class FormPanel extends PureComponent {
  constructor() {
    super();
    this.state = {
      height: null
    };
    this.handleWindowResize = throttle(this.setHeightToState.bind(this), 100);
  }
  componentDidMount() {
    this.setHeightToState();
    window.addEventListener('resize', this.handleWindowResize);
  }
  componentDidUpdate(prevProps) {
    if (this.props.selectedFormItemReference !== prevProps.selectedFormItemReference) {
      scrollToPosition('.properties-search-section');
    }
  }
  componentWillUnmount() {
    this.props.hideConfigurationPanel({
      updateRoute: false
    });
    window.removeEventListener('resize', this.handleWindowResize);
  }
  setHeightToState() {
    let offset = 0;
    const container = document.querySelector('[data-test-id="form-editor-container"]');
    const navBar = document.querySelector('[data-test-id="navigation-bar-body"]');
    if (navBar) {
      const {
        top: navBarTop,
        height: navBarHeight
      } = navBar.getBoundingClientRect();
      offset = navBarTop + navBarHeight;
    }
    const styles = getComputedStyle(container, null);
    const newHeight = parseFloat(styles.height.replace('px', ''));
    this.setState({
      height: newHeight - offset
    });
  }
  getStyles() {
    const {
      height
    } = this.state;
    const styles = {};
    if (height) {
      styles.height = height;
      styles.overflow = 'auto';
    }
    return styles;
  }
  maybeRenderQueuedFieldsPanel() {
    const {
      selectedFormItemType,
      selectedFormItemReference,
      handleFormFieldSelect,
      handleRichTextSelect,
      formType,
      ungatedForPagination
    } = this.props;
    if (!ungatedForPagination && formType !== FormTypes.TICKET_FORM && this.props.hasProgressiveProfillingAccess) {
      return /*#__PURE__*/_jsx(Box, {
        inlineSize: "100%",
        children: /*#__PURE__*/_jsx(QueuedFieldsPanel, {
          selectedFormItemType: selectedFormItemType,
          selectedFormItemReference: selectedFormItemReference,
          handleFormFieldSelect: handleFormFieldSelect,
          handleRichTextSelect: handleRichTextSelect
        })
      });
    }
    return null;
  }
  renderSecondaryPanel() {
    const {
      formGuid,
      hideConfigurationPanel,
      selectedFormItemReference,
      selectedFormItemType,
      updateFieldValidation,
      updateForm,
      updateFormField,
      updateFormFieldGroup
    } = this.props;
    const content = getFormFieldByType({
      formGuid,
      hideConfigurationPanel,
      selectedFormItemReference,
      selectedFormItemType,
      updateFieldValidation,
      updateForm,
      updateFormField,
      updateFormFieldGroup
    });
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "FORM_FIELD_EDITOR_PANEL_LOADED",
      children: /*#__PURE__*/_jsx(SecondaryPanelContainer, {
        hasContent: Boolean(content),
        children: content
      })
    });
  }
  render() {
    const {
      formType,
      handleCaptchaSelect,
      handleFormFieldSelect,
      handleRichTextSelect,
      handleSubmitSelect,
      selectedFormItemReference,
      selectedFormItemType,
      step
    } = this.props;
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "FORM_EDITOR_PANELS_LOADED",
      children: /*#__PURE__*/_jsxs(Grid, {
        children: [/*#__PURE__*/_jsx(GridItem, {
          paddingInlineEnd: "0",
          size: {
            md: 5
          },
          children: /*#__PURE__*/_jsxs(Wrapper, {
            id: "form-editor-side-panel",
            "data-onboarding": "form-editor-side-panel",
            height: this.getStyles().height,
            overflow: this.getStyles().overflow,
            children: [/*#__PURE__*/_jsx(PropertiesContainer, {
              step: step
            }), this.renderSecondaryPanel()]
          })
        }), /*#__PURE__*/_jsx(GridItem, {
          paddingInlineStart: "0",
          size: {
            md: 7
          },
          children: /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-surface-raised",
            children: /*#__PURE__*/_jsx(EditorPanel, {
              id: "form-editor-main-panel",
              "data-onboarding": "commentable-areas",
              "data-commenting-scroll-container": true,
              height: this.getStyles().height,
              overflow: this.getStyles().overflow,
              children: /*#__PURE__*/_jsxs(TFlex, {
                direction: "column",
                justify: "between",
                blockSize: "100%",
                children: [/*#__PURE__*/_jsx(Box, {
                  inlineSize: "100%",
                  children: /*#__PURE__*/_jsx(FormFieldsPanel, {
                    handleFormFieldSelect: handleFormFieldSelect,
                    selectedFormItemType: selectedFormItemType,
                    selectedFormItemReference: selectedFormItemReference,
                    handleSubmitSelect: handleSubmitSelect,
                    handleRichTextSelect: handleRichTextSelect,
                    handleCaptchaSelect: handleCaptchaSelect,
                    formType: formType
                  })
                }), /*#__PURE__*/_jsx(DeadSpaceDropZone, {}), this.maybeRenderQueuedFieldsPanel()]
              })
            })
          })
        }), /*#__PURE__*/_jsx(CustomDragLayer, {}), /*#__PURE__*/_jsx(CustomFieldDragLayer, {})]
      })
    });
  }
}
FormPanel.propTypes = {
  selectedFormItemType: PropTypes.oneOf([...Object.values(FieldTypes || {}), ...Object.values(SpecialFieldTypes || {})]),
  selectedFormItemReference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleFormFieldSelect: PropTypes.func.isRequired,
  handleSubmitSelect: PropTypes.func.isRequired,
  handleRichTextSelect: PropTypes.func.isRequired,
  handleCaptchaSelect: PropTypes.func.isRequired,
  handleAddStep: PropTypes.func.isRequired,
  hideConfigurationPanel: PropTypes.func.isRequired,
  updateForm: PropTypes.func.isRequired,
  updateFormField: PropTypes.func.isRequired,
  updateFieldValidation: PropTypes.func.isRequired,
  updateFormFieldGroup: PropTypes.func.isRequired,
  hasProgressiveProfillingAccess: PropTypes.bool.isRequired,
  formType: PropTypes.string.isRequired,
  ungatedForPagination: PropTypes.bool.isRequired,
  formGuid: PropTypes.string,
  step: PropTypes.string
};
export default FormPanel;